import { forwardRef } from 'react';
import { cn } from '../../utils/cn';

// Switch — accessible role="switch" toggle.
// Visual baseline: track h-6 w-11 (md) / h-5 w-9 (sm), brand-500 active, white thumb.
// Props:
//   - checked: boolean
//   - onChange: (next: boolean) => void
//   - id?: string
//   - label?: ReactNode  (renders next to the switch)
//   - size?: 'sm' | 'md'
//   - tone?: 'brand' (default) — placeholder for future variants
//   - disabled?: boolean
//   - className?: string  (merged on the outer wrapper)
//   - ariaLabel?: string  (used when no label is rendered)

const SIZES = {
  sm: {
    track: 'h-5 w-9',
    thumb: 'h-3.5 w-3.5',
    on: 'translate-x-[18px]',
    off: 'translate-x-0.5',
    // Área táctil ≥44px sin deformar el track (el hit-area se expande con un
    // pseudo-elemento; la regla global de 44px excluye [role=switch]).
    hit: 'before:-inset-3',
  },
  md: {
    track: 'h-6 w-11',
    thumb: 'h-4 w-4',
    on: 'translate-x-6',
    off: 'translate-x-1',
    hit: 'before:-inset-2.5',
  },
};

export const Switch = forwardRef(function Switch(
  {
    checked,
    onChange,
    id,
    label,
    size = 'md',
    disabled,
    className,
    ariaLabel,
    ...rest
  },
  ref,
) {
  const s = SIZES[size] ?? SIZES.md;
  const handleClick = () => {
    if (disabled) return;
    onChange?.(!checked);
  };
  const btn = (
    <button
      ref={ref}
      type="button"
      role="switch"
      id={id}
      aria-checked={!!checked}
      aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        "before:absolute before:content-[''] before:rounded-full",
        s.hit,
        s.track,
        checked ? 'bg-brand-500' : 'bg-bg-subtle border border-border',
        !label && className,
      )}
      {...rest}
    >
      <span
        className={cn(
          'inline-block transform rounded-full bg-white shadow transition-transform',
          s.thumb,
          checked ? s.on : s.off,
        )}
      />
    </button>
  );

  if (!label) return btn;

  return (
    <label
      htmlFor={id}
      className={cn(
        'inline-flex items-center gap-2 cursor-pointer select-none',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      {btn}
      <span className="text-sm text-fg">{label}</span>
    </label>
  );
});

Switch.displayName = 'Switch';

export default Switch;
